import { Fragment } from 'react';
import { Text, View, StyleSheet, Link } from '@react-pdf/renderer';
import { distanceToString } from 'utilities/format';
import equipmentCatalog from 'globalConstants/EquipmentCatalog';
import { keys, head, toUpper, sum, mapValues, isEmpty, round, find, some, intersectionWith, differenceWith, flatten } from 'lodash';

const styles = StyleSheet.create({
  line: {
    flexDirection: 'row',
    justifyContent: 'start',
  },

  header: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#d7282f',
    textAlign: 'center',
    borderRightWidth: 1,
    borderRightColor: '#a5aaad',
    borderBottomWidth: 0,

  },

  cell: {
    fontSize: 9,
    paddingTop: 2,
    borderRightWidth: 1,
    borderRightColor: '#a5aaad',
    borderBottomWidth: 0,
  },

  footnoteTitle: {
    fontSize: 8,
    color: "#d7282f",
    paddingTop: 4,
    paddingLeft: 10
  },
  footnote: {
    fontSize: 8,
    color: "#d7282f",
    paddingLeft: 20
  },
});

/**
* @function deviceAdjustment
* @description Helper function that adjusts device positions relative to walls
* @param {Object} device - Device to adjust
* @param {Object} dimensions - Room dimensions
* @param {Object} center - Room center point
* @returns {Object} Adjusted device data
*/
const deviceAdjustment = (device, dimensions, center) => {
  const { x, y } = device.location;
  const { length: cx, width: cy } = mapValues(dimensions, (d) => d / 2);
  const adjustedLocation = { x, y };

  // Calculate the angle of the device in degrees
  let angle = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 180;
  angle = angle % 360;

  // Calculate the room diagonal angle
  const roomDiagonal = Math.atan2(-cy, -cx) * (180 / Math.PI) + 180;

  // Determine barSide and calculate adjustments
  if (angle <= roomDiagonal) { // left side
    adjustedLocation.x = 0;
  } else if (angle <= 180 - roomDiagonal) { // top side
    adjustedLocation.y = 0;
  } else if (angle <= 180 + roomDiagonal) { // right side
    adjustedLocation.x = dimensions.length;
  } else if (angle <= 360 - roomDiagonal) { // bottom side
    adjustedLocation.y = dimensions.width;
  } else { // left side (wrap aroound)
    adjustedLocation.x = 0;
  };

  let horAngle;
  if (!isEmpty(center)) {
    const aimx = cx + center.x;
    const aimy = cy + center.y;
    horAngle = Math.atan2((adjustedLocation.y - aimy), (aimx - adjustedLocation.x)) * (180 / Math.PI);
    horAngle = round((360 - horAngle) % 360);
  };

  return {
    ...device,
    location: adjustedLocation,
    ...isFinite(horAngle) && { horAngle }
  }
};

/**
* @function tableLists
* @description Collection of functions to generate different table types
* @property {Function} positioningDetail - Generates positioning details table
* @property {Function} areaEquipmentList - Generates area equipment list
* @property {Function} speakerSummary - Generates speaker summary with links
*/
const tableLists = {
  positioningDetail: (deviceList, isMeter, adjust, dimensions, center) => {
    const tableData = deviceList.reduce((acc, curr, index) => {
      const adjustedDevice = adjust ? deviceAdjustment(curr, dimensions, center) : curr;
      acc.push({
        '#': `${index + 1}`,
        'model': curr.modelName,
        'horizontal\ndist (x)': distanceToString(adjustedDevice.location.x, isMeter),
        'vertical\ndist (y)': distanceToString(adjustedDevice.location.y, isMeter),
        ...isFinite(adjustedDevice.location.z) && { 'height\n(z)': distanceToString(adjustedDevice.location.z, isMeter) },
        ...isFinite(adjustedDevice.horAngle) && { 'horizontal\nangle': `${adjustedDevice.horAngle} deg` },
        ...isFinite(adjustedDevice.verAngle) && { 'vertical\nangle': `${adjustedDevice.verAngle} deg` },
        ...isFinite(curr.powerTap70V) && { '70v Tap': `${curr.powerTap70V} Watts` },
        ...isFinite(curr.gain) && { 'gain': `${curr.gain} dB` },
        ...isFinite(curr.delay) && { 'delay': `${curr.delay} ms` }
      });
      return acc
    }, []);
    return tableData
  },
  areaEquipmentList: (deviceList) => deviceList,
  speakerSummary: (speakerList) => {
    let deviceList = [...speakerList];

    // pre-process doubles 
    // Helper function to check if they are close together
    const isClose = (speaker1, speaker2) => {
      const dx = speaker2.location.x - speaker1.location.x;
      const dy = speaker2.location.y - speaker1.location.y;
      const dz = speaker2.location.z - speaker1.location.z;
      return Math.sqrt(dx * dx + dy * dy + dz * dz) <= 1;
    };

    // Only do this if there are CCA-80s
    if (some(deviceList, device => device.modelName === 'CCA-80')) {
      const facingRight = deviceList.filter(device => device.modelName === 'CCA-80' && device.horAngle < 180);
      const facingLeft = deviceList.filter(device => device.modelName === 'CCA-80' && device.horAngle >= 180);

      /**
      * @description This section changes CCA-80 speakers for CCA-80 when:
      * a: speakers are facing oposite direction
      * b: speakers are closer that 1m 
      * This ensures product recomendation match the best solution when using the CCA-80 in a center-out alignment
      * This function is the same as in calculateEquipmentTable in logic.js 
      * Duplicated as PDF can't access the store
      * Ideally this should be refactored so the equipment list is set in the store (similar to CRD) 
      * and the resulting table ius pulled instead of calculating it 
      */
      // find pairs 
      // intersectionWith only returns facing right speakers =
      const matchedPairs = intersectionWith(facingRight, facingLeft, isClose);
      const pairs = matchedPairs.map(speaker => (
        [speaker, facingLeft.find(leftSPeaker => isClose(speaker, leftSPeaker))]
      ));

      // remove pairs from main array 
      deviceList = differenceWith(deviceList, flatten(pairs));

      // Reduce the pairs and add to list
      const newPairs = Array.from(pairs, pair => ({
        modelName: 'CCA-80D'
      }));
      deviceList = deviceList.concat(newPairs);
    };


    const tableData = deviceList.reduce((acc, curr) => {
      const prod = acc.find(x => x.product.name === curr.modelName);
      if (prod) {
        prod.quantity++;
      } else {
        const spkr = find(equipmentCatalog.Speakers, spkr => spkr.modelName === curr.modelName);
        acc.push({
          quantity: 1,
          product: { name: spkr.modelName, link: `https://products.biamp.com/product-details/-/o/ecom-item/${spkr.partNumber}` },
          description: spkr.description,
        })
      };
      return acc
    }, []);
    return tableData
  },
};


/**
* @function Table
* @description Renders formatted tables in PDF documents for equipment lists,
* positioning details, and speaker summaries.
* 
* @param {Object} props
* @param {Object} props.content - Table content data
* @param {Array<Object>} props.content.listData - Data to display in table
* @param {boolean} props.content.isMeter - Whether to use metric units
* @param {boolean} [props.content.adjust] - Whether to adjust device positions
* @param {Object} [props.content.dimensions] - Room dimensions for adjustments
* @param {Object} [props.content.center] - Center point for angle calculations
* @param {Object} props.constants - Table configuration
* @param {string} props.constants.list - Type of list to generate
* @param {Array<number>} props.constants.widths - Column width percentages
* @param {Array<string>} [props.constants.notes] - Footer notes
* 
* @example
* <Table 
*   content={{
*     listData: [{modelName: 'SP100', location: {x: 5, y: 3}}],
*     isMeter: true,
*     dimensions: {length: 20, width: 15}
*   }}
*   constants={{
*     list: 'positioningDetail',
*     widths: [10, 20, 20, 20, 30],
*     notes: ['All measurements in meters']
*   }}
* />
* 
* @note
* - Uses @react-pdf/renderer components
* - Supports different table types through tableLists
* - Auto-adjusts column widths to 100%
* - Handles metric/imperial unit conversion
* - Includes product links to Biamp website
* - Supports wall-relative positioning
* - Maintains consistent styling with borders/colors
*/
const Table = (props) => {
  const { content: { listData, isMeter, adjust = false, dimensions, center = {} }, constants: { list, widths, notes = [] } } = props;
  const data = tableLists[list](listData, isMeter, adjust, dimensions, center);
  const headers = keys(head(data));
  const tableWidth = sum(widths);
  const adjustedWidths = (tableWidth < 100 ? widths.map(w => w * 100 / tableWidth) : widths).slice(0, headers.length);

  return (
    <Fragment>
      <View style={styles.line} wrap={false}>
        {headers.map((title, index, { length }) => {
          const style = [styles.header, {
            width: `${adjustedWidths[index]}%`,
            borderRightColor: `${index < length - 1 ? '#a5aaad' : '#fff'}`
          }]
          return (<Text key={`title-${index}`} style={style} >{title === '#' ? '#' : toUpper(title)}</Text>)
        })}
      </View>

      {data.map((item, index) => {
        return (
          <View key={`line-${index}`} style={styles.line} wrap={false} >
            {headers.map((title, jndex, { length }) => {
              const style = [styles.cell, {
                width: `${adjustedWidths[jndex]}%`,
                backgroundColor: `${index % 2 === 0 ? '#e6e7e8' : '#fff'}`,
                borderRightColor: `${jndex < length - 1 ? '#a5aaad' : index % 2 === 0 ? '#e6e7e8' : '#fff'}`,
                textAlign: `${['description', 'product'].includes(title) ? 'start' : 'center'}`,
                borderBottomColor: '#a5aaad',
                borderBottomWidth: 1,
              }];
              const cellContent = title === '#' ? index + 1 : item[title];
              const key = `cell-${index}-${jndex}`
              return (
                title === 'product' ? <Link key={key} style={[style, { color: "#d7282f" }]} src={item[title].link} >{item[title].name}</Link> : <Text key={key} style={style} >{cellContent}</Text>
              )
            }
            )}
          </View>
        )
      })}

      {notes.length > 0 &&
        <View wrap={false}>
          <Text style={styles.footnoteTitle}>Notes:</Text>
          {notes.map((note, index) => <Text key={`note=${index}`} style={styles.footnote}>{note}</Text>)}
        </View>}
    </Fragment>
  )
};

export default Table;